import React, { useEffect, useState } from 'react';
import SideNavigationBar from '../components/SideNavigationBar';
import {
	FileUploadDiv,
	helperText,
	useMainCustomStyle,
} from '../assets/theme/Theme';
import {
	CircularProgress,
	Grid,
	MenuItem,
	Select,
	Typography,
	IconButton as IconBtn,
} from '@mui/material';
import TextInput from '../components/TextInput';
import Dropzone from 'react-dropzone';
import { ConnectAPi, UploadIcon } from '../assets/icons/Icons';
import IconButton from '../components/IconButton';
import { addResident } from '../services/CarePlanServices';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getEnabledUserFeatures } from '../context/MainProvider';
import { FEATURE_TYPES } from '../Utils/Enums';
import {
	addElderDetails,
	sendElderDetailsForExtraction,
} from '../services/ElderService';
import { getAccessToken, getElderHomeId, getUserId } from '../Utils/AuthUtil';
import AlertBanner from '../components/AlertBanner';
import { useNavigate } from 'react-router-dom';

function AddResident() {
	const navigate = useNavigate();
	const mainDivStyle = useMainCustomStyle();
	const [id, setId] = useState('');
	const [name, setName] = useState('');
	const [txt, setTxt] = useState('');
	const [fileData, setFileData] = useState('');
	const [idError, setIdError] = useState({ status: false, msg: '' });
	const [nameError, setNameError] = useState({ status: false, msg: '' });
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [isConnectApiEnabled, setIsConnectApiEnabled] = useState(true);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[0].key,
	);
	const [showICPBanner, setShowICPBanner] = useState(false);
	const [bannerMsg, setBannerMsg] = useState('');

	function validateInputs() {
		let isValid = true;

		if (!name || name === '') {
			setNameError({ status: true, msg: 'Enter resident name' });
			isValid = false;
		}

		if (!txt) {
			setTxtError({ status: true, msg: 'Upload the text file' });
			isValid = false;
		}

		return isValid;
	}

	async function onAddResident() {
		const isInputsValid = validateInputs();

		if (isInputsValid) {
			setIsLoading(true);

			const formData = new FormData();
			formData.append(
				'elder_details_file',
				new Blob([fileData], { type: 'text/plain' }),
				fileData.name,
			);
			formData.append('resident_name', name);
			formData.append('client_id', getElderHomeId());

			const data = await sendElderDetailsForExtraction(formData);

			if (!data.error) {
				storeElderData(data);
			} else {
				toast.error(data.message);
				setIsLoading(false);
			}
		}
	}

	async function storeElderData(data) {
		const token = getAccessToken();
		const elderData = data.elder_info.Profile;
		const ignoringData = ['DateOfBirth', 'Age', 'Name', 'Gender'];

		let elderHistory = {};
		Object.keys(elderData).map(key => {
			if (!ignoringData.includes(key)) {
				const currentValue = Array.isArray(elderData[key])
					? elderData[key]
					: [elderData[key]];
				elderHistory = { ...elderHistory, [key]: currentValue };
			}
		});

		const newElderData = {
			elderHomeId: getElderHomeId(),
			adminId: getUserId(),
			name: name,
			gender: elderData.Gender ? elderData.Gender : '',
			dateOfBirth: elderData.DateOfBirth ? elderData.DateOfBirth : 'N/A',
			age: elderData.Age ? parseInt(elderData.Age) : 0,
			elderHistory: elderHistory,
		};

		const formData = new FormData();
		formData.append(
			'elder',
			new Blob([JSON.stringify(newElderData)], {
				type: 'application/json',
			}),
		);
		formData.append('file', fileData);

		const response = await addElderDetails(formData, token);

		if (!response.error) {
			setBannerMsg(
				`Resident ${name} has been successfully onboarded. As the next step, you can generate their initial care plan.`,
			);
			setShowICPBanner(true);
			setName('');
			setTxt('');
			setFileData('');
			toast.success('Resident added successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
		} else {
			toast.error(response.message);
		}

		setIsLoading(false);
	}

	function readTextFile(e) {
		setTxt(e[0]);
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}
					paddingRight={'20px'}>
					<Grid item paddingLeft="30px" paddingBottom={'20px'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Onboarding a Resident
						</Typography>
					</Grid>
					<Grid
						container
						marginLeft={'30px'}
						paddingX={'20px'}
						bgcolor={'#FFFFFF'}
						borderRadius={'10px'}
						justifyContent={'space-between'}
						style={
							isThisFeatureDisabled
								? {
										filter: 'blur(0.8px)',
										pointerEvents: 'none',
									}
								: {}
						}>
						<Grid item xs={12} md={6} paddingTop="20px">
							<Typography
								fontSize={'16px'}
								color={'#7C7C7C'}
								paddingBottom="10px">
								Name
							</Typography>
							<TextInput
								error={nameError.status}
								width="100%"
								placeholder={'Enter resident name'}
								value={name}
								onChange={value => {
									setName(value);
									setNameError({ status: false, msg: '' });
								}}
								disabled={isThisFeatureDisabled}
							/>
							<Typography style={helperText}>
								{nameError.msg}
							</Typography>
						</Grid>
						<Grid container>
							<Grid item width={'100%'} paddingTop="15px">
								<Dropzone
									onDrop={acceptedFiles =>
										readTextFile(acceptedFiles)
									}>
									{({ getRootProps, getInputProps }) => (
										<section>
											<div
												{...getRootProps()}
												style={{
													...FileUploadDiv,
													border: txtError.status
														? '1px solid red'
														: FileUploadDiv.border,
												}}>
												<input {...getInputProps()} />
												<UploadIcon />
												<br />
												{!txt ? (
													<>
														<Typography
															fontSize={'16px'}
															fontWeight={'700'}
															color={'#7C7C7C'}>
															Choose a file or
															drag & drop here
														</Typography>
														<Typography
															fontSize={'14px'}
															color={'#B4B2B2'}>
															Maximum upload file
															size: 5MB
														</Typography>
													</>
												) : (
													<Typography variant="fileUploadBoxSubText">
														{txt.name}
													</Typography>
												)}
											</div>
										</section>
									)}
								</Dropzone>
								<Typography style={helperText}>
									{txtError.msg}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
					<Grid
						container
						width={'100%'}
						justifyContent={'flex-end'}
						alignItems={'center'}
						marginY={'10px'}
						style={
							isThisFeatureDisabled
								? {
										filter: 'blur(0.8px)',
										pointerEvents: 'none',
									}
								: {}
						}>
						{isLoading && (
							<Grid paddingRight={'10px'}>
								<CircularProgress
									sx={{ color: '#28696D' }}
									size={'2rem'}
								/>
							</Grid>
						)}
						<IconButton text={'Continue'} onClick={onAddResident} />
					</Grid>
					{showICPBanner && (
						<Grid
							container
							sx={{
								borderRadius: '5px',
								marginLeft: '30px',
								marginY: '10px',
							}}>
							<AlertBanner
								content={bannerMsg}
								btnOneTxt={'Generate Initial Plan'}
								handleButtonOneClick={() =>
									navigate('/initialCarePlan')
								}
								displayBtnOne={true}
							/>
						</Grid>
					)}
					<Grid
						container
						sx={{
							borderRadius: '5px',
							marginLeft: '30px',
							marginY: '10px',
						}}>
						<AlertBanner
							content={
								'Onboard resident through API integration control panel'
							}
							btnOneTxt={'Connect Api'}
							handleButtonOneClick={() =>
								navigate('/integration')
							}
							displayBtnOne={true}
						/>
					</Grid>
					{/* <Grid
						container
						marginLeft={'30px'}
						paddingY={'10px'}
						style={
							isConnectApiEnabled
								? {
										filter: 'blur(0.8px)',
										pointerEvents: 'none',
									}
								: {}
						}>
						<IconBtn
							disableRipple
							// onClick={() => onClickFirstBtn('API')}
							style={{
								backgroundColor: 'transparent',
								padding: '0px',
								width: '100%',
							}}>
							<Grid
								container
								height={'130px'}
								padding={'10px'}
								borderRadius={'15px'}
								bgcolor={'#FFFFFF'}
								// bgcolor={
								// 	popupBtnState === 'Connect'
								// 		? '#28696D'
								// 		: '#FFFFFF'
								// }
								justifyContent={'center'}
								alignItems={'center'}>
								<ConnectAPi />
								<Typography
									variant="h6"
									marginLeft={'10px'}
									fontWeight={'500'}
									// color={
									// 	popupBtnState === 'Connect'
									// 		? '#FFFFFF'
									// 		: '#28696D'
									// }
									color={'#28696D'}>
									Connect Your Api
								</Typography>
							</Grid>
						</IconBtn>
					</Grid> */}
				</Grid>
			</div>
		</>
	);
}

export default AddResident;
